import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { LegendItem } from '../../../../../common/chart/legend/LegendItem'
import { PieChartContainer } from '../../../../../common/chart/pieChart'
import { Element } from '../../../../../common/html/element'
import { SCREEN } from '../../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../../common/selectCurrencyUnit/useFormatCurrency'
import { Tabs } from '../../../../../common/tabs'
import { getAppendStyleItemWithCustom } from '../../../../../common/tabs/helper'
import { getUnique, keyBy } from '../../../../../helpers/common'
import { formatVal, valToPercent } from '../../../../../helpers/value'
import {
  getNoDataComponent,
  getStyleHeightContainer,
  getYearTabs,
  handleIconDownload,
} from '../../helper'
import classes from '../../index.module.scss'
import { changeYear, selectYear } from '../../store/slice'
import {
  DEPT_TO_LIABILITIES_CODE,
  LABEL_CHART_DATA,
  PIECHART_DATA,
  TOTAL_EQUITY_CODE,
  TOTAL_LIABILITIES_CODE,
} from './constant'
import {
  selectDataViewByEachYear,
  selectIndicatorById,
  selectYears,
} from './store/slice'

const mapNameId = {
  FA160: 'BS95',
  FA159: 'BS65',
}

const EachYear = ({
  id,
  isDynamicReport,
  year: yearSetting,
  isDownloadComponent,
}) => {
  const chartId = useRef(getUnique())

  const dispatch = useDispatch()
  const locale = useSelector((state) => state.i18n.locale)
  const {
    formatCurrencyWithUnit,
    currency,
    getExchangeRate,
    multiplier,
    formatedUnit,
  } = useFormatCurrency(SCREEN.FINANCIAL_ANALYSIS)
  const year = useSelector(selectYear(id, yearSetting))
  const data = useSelector(selectDataViewByEachYear(year))
  const dataById = keyBy(data, 'id')
  const indicatorById = useSelector(selectIndicatorById)
  const years = useSelector(selectYears)

  const tabs = getYearTabs(years)

  const setYear = (year) => dispatch(changeYear({ id, year }))

  useEffect(() => {
    if (
      (tabs.length && !year) ||
      !tabs.map((item) => item.key).includes(String(year))
    ) {
      setYear(tabs[tabs.length - 1]?.key)
    }
  }, [tabs.length, year])

  const pieChartData = PIECHART_DATA.map((item) => ({
    ...item,
    name: indicatorById[mapNameId[item.id]]?.ratioName,
    value: dataById[item.id]?.value || 0,
    color: item.color,
  }))

  const minValueTextCenter = 0.2
  const deptToLiabilitiesValue = dataById[DEPT_TO_LIABILITIES_CODE]?.value

  const exchangeRate = getExchangeRate({
    year,
    exchangeRateType: indicatorById[TOTAL_EQUITY_CODE]?.exchangeRateType,
  })
  const totalPieValue =
    ((dataById[TOTAL_EQUITY_CODE]?.value +
      dataById[TOTAL_LIABILITIES_CODE]?.value) /
      exchangeRate) *
    multiplier

  const isNoData = !data?.length

  useEffect(() => {
    handleIconDownload(id, isNoData)
  }, [isNoData])

  if (!year) return getNoDataComponent(isDynamicReport)

  return (
    <div
      style={{
        height: getStyleHeightContainer(isDynamicReport, isNoData, 280),
      }}
      className={`flexColumn ${classes.solvencyEachYearContainer}`}
    >
      <Element
        style={{
          borderBottom: !isDynamicReport ? '1px solid #B5CEF8' : undefined,
        }}
        className={`dFlex justifyContentEnd ${classes.tabContainer}`}
      >
        {!isDynamicReport ? (
          <Tabs
            activeKey={year}
            items={tabs}
            tabListClassName={classes.tabs}
            getAppendStyleItem={getAppendStyleItemWithCustom({ width: '40px' })}
            isScrollContent={false}
            onChange={setYear}
          />
        ) : (
          <>
            {!isNoData && (
              <div className="fw600 py1 w100">
                <Translate value="common.FISCAL_YEAR" />: {year}
              </div>
            )}
          </>
        )}
      </Element>
      {!data?.length ? (
        getNoDataComponent(isDynamicReport)
      ) : (
        <div className="h100 dFlex">
          <div className="h100 w30 flexColumn">
            <div className="h100 pt2 dFlex justifyContentCenter">
              <PieChartContainer
                chartId={chartId.current}
                data={pieChartData}
                width={150}
                height={150}
                isLabelActive={false}
                innerRadius={32}
                formatValueTooltip={(val) =>
                  formatVal(
                    val * totalPieValue,
                    locale,
                    undefined,
                    currency === 'VND' ? 0 : 2,
                  ) +
                  ' ' +
                  formatedUnit
                }
              />
            </div>
            <div className="fw600">
              <Translate value="financialAnalysis.CAPITAL_STRUCTURE" />
            </div>
            <div className={classes.mt4}>
              {pieChartData.map((item, index) => (
                <Element
                  key={index}
                  style={{ backgroundColor: item.bgColor }}
                  className={classes.itemFooterContainer}
                >
                  <LegendItem
                    chartId={chartId.current}
                    color={item.color}
                    title={item.title}
                    isI18n
                    pieIndex={index}
                    isEllipsis={!isDynamicReport && !isDownloadComponent}
                  />
                  <Element style={{ color: item.textColor }}>
                    {valToPercent(item.value, locale)}
                  </Element>
                </Element>
              ))}
            </div>
          </div>
          <div className="h100 w70 pl1 flexColumn">
            <div className="h100 pt2 dFlex justifyContentBetween">
              {LABEL_CHART_DATA.map((item, index) => (
                <Element
                  key={index}
                  style={{ backgroundColor: '#DDEDF5', color: '#191D25' }}
                  className={`${classes.totalItem} flexColumn`}
                >
                  <div className="fw600 textCenter">
                    <Translate value={item.topTitle} />
                  </div>
                  <Element
                    style={{ backgroundColor: '#9FB7C5' }}
                    className={classes.border}
                  />
                  <div className="fw600 textCenter">
                    <Translate value={item.bottomTitle} />
                  </div>
                  <div className="h100 dFlex alignItemsEnd justifyContentCenter pb1">
                    <Element
                      style={{
                        backgroundColor: '#FFFFFF',
                        fontSize: 20,
                        width: isDynamicReport ? 80 : undefined,
                        height: isDynamicReport ? 80 : undefined,
                      }}
                      className={classes.circle}
                    >
                      {formatVal(dataById[item.id]?.value, locale)}
                    </Element>
                  </div>
                </Element>
              ))}
            </div>
            <div>
              <div className="fw600 pt1">
                <Translate value="financialAnalysis.TOTAL_DEBT" /> /{' '}
                <Translate value="financialAnalysis.TOTAL_LIABILITIES" />
              </div>
              <div className={classes.totalBar}>
                <Element
                  style={{
                    backgroundColor: '#006AA6',
                    width: deptToLiabilitiesValue * 100 + '%',
                    color: '#FFFFFF',
                  }}
                  className={classes.value}
                >
                  {deptToLiabilitiesValue > minValueTextCenter &&
                    valToPercent(deptToLiabilitiesValue, locale)}
                </Element>
                <Element
                  style={{
                    backgroundColor: '#DDEDF5',
                    width: 100 - deptToLiabilitiesValue * 100 + '%',
                    paddingLeft:
                      deptToLiabilitiesValue <= minValueTextCenter ? 4 : 0,
                  }}
                  className="fw600 dFlex alignItemsCenter"
                >
                  {deptToLiabilitiesValue <= minValueTextCenter &&
                    valToPercent(deptToLiabilitiesValue, locale)}
                </Element>
              </div>
              <div className="fw600 pt1 textEnd">
                <Translate value="financialAnalysis.TOTAL_DEBT" />:{' '}
                {formatCurrencyWithUnit({ value: 81234987012235 })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EachYear
